import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Tag, Tooltip, Typography, Skeleton, Empty, Segmented, Select, Button, Space, Popconfirm, Checkbox, Popover } from 'antd';
import { List as VirtualList } from 'react-window';
import { LeftOutlined, ReloadOutlined, DownloadOutlined, QuestionCircleOutlined, SyncOutlined, LockOutlined, InfoCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import type { Issue, StatusFilter, SortOrder, TriageActionDef } from '../../types/triage';
import { FLEET_CLASS_IDS, TRIAGE_ACTIONS } from '../../types/triage';
import {
  nodeTypeIcon,
  PENDING_CLASS_TAG,
  classTagMap,
  classTooltipMap,
  priorityColor,
  priorityLabel,
  shortActor,
  isClaimFresh,
} from '../triageMaps';
import SavedViews from '../SavedViews';
import Sparkline from '../Sparkline';
import { fetchSparklines } from '../../config/api';

type SparklineData = Record<string, { battery: (number | null)[]; signal: (number | null)[] }>;

dayjs.extend(relativeTime);

interface IssueListPaneProps {
  issues: Issue[];
  loading: boolean;
  selectedIssueId: number | null;
  statusFilter: StatusFilter;
  classFilter: number | null;
  sortOrder: SortOrder;
  mode?: 'nodes' | 'issues';
  // Current operator's email/sub, so a claim held by "me" reads "you" and the
  // claim chip is styled as mine vs someone else's.
  currentUserEmail?: string | null;
  // Bulk selection (confirmed /issues view). When selectable, rows show a
  // checkbox and a bulk-action bar appears once anything is picked.
  selectable?: boolean;
  selectedIds?: Set<number>;
  onToggleSelect?: (id: number) => void;
  onClearSelection?: () => void;
  onBulkAction?: (action: TriageActionDef) => void;
  bulkRunning?: boolean;
  onSelectIssue: (id: number | null) => void;
  onStatusFilterChange: (val: StatusFilter) => void;
  onClassFilterChange: (val: number | null) => void;
  onSortChange: (val: SortOrder) => void;
  onBackfill?: () => void;
  backfillRunning?: boolean;
  onReclassifyAll?: (opts: { skipLlm: boolean }) => void;
  reclassifyAllRunning?: boolean;
  onCollapse?: () => void;
  hasMore?: boolean;
  loadingMore?: boolean;
  onLoadMore?: () => void;
  // Authoritative count from /api/locations rollup, when available. Lets us
  // show "200 of 752 to triage" instead of "200+ to triage" — the loaded list
  // alone can't tell you the queue depth past the current page.
  totalCount?: number;
  // Apply a saved filter view. Parent owns the URL/query state, so the pane
  // just hands back the picked combination.
  onApplyView?: (view: { status: string | null; class: string | null; sort: string | null }) => void;
}

const ROW_HEIGHT = 72;

// Plain-language legend for the row chips, shown from a "?" popover in the list
// header. Operators told us the row was cryptic; this spells each mark out.
const CHIP_LEGEND: { label: string; meaning: string }[] = [
  { label: 'P1–P4', meaning: 'Priority — P1 critical, P4 low. Queue is sorted by this.' },
  { label: '⏱ age (red)', meaning: 'Open longer than its priority allows (P1 > 24h, P2 > 7d, else > 14d).' },
  { label: '🔒 name / you', meaning: 'Someone is investigating this right now (claimed).' },
  { label: 'Class tag', meaning: 'Suggested issue class (e.g. Hardware). Shown on Issues; hidden on Triage so you decide fresh.' },
];

const IssueRow = ({
  index,
  style,
  issues,
  selectedIssueId,
  selectIssue,
  sparklines,
  hideClassChip,
  currentUserEmail,
  selectable,
  selectedIds,
  onToggleSelect,
}: {
  index: number;
  style: React.CSSProperties;
  issues: Issue[];
  selectedIssueId: number | null;
  selectIssue: (id: number) => void;
  sparklines: SparklineData;
  selectable?: boolean;
  selectedIds?: Set<number>;
  onToggleSelect?: (id: number) => void;
  // /triage mode hides the class + confidence chips on each row. They're
  // the rule classifier's tentative guesses, and surfacing them in the
  // queue conflicted with the "triage = first read, you decide" framing.
  // The chips still render on /issues (post-confirm) and in the detail
  // pane (where the operator needs the suggestion to make a decision).
  hideClassChip: boolean;
  currentUserEmail?: string | null;
}) => {
  const issue = issues[index];
  if (!issue) return <div style={style} />;

  const classTag = issue.ticket_class_id != null ? classTagMap[issue.ticket_class_id] : null;
  const classTooltip = issue.ticket_class_id != null
    ? classTooltipMap[issue.ticket_class_id] || issue.ticket_class
    : 'Unclassified';
  // Coerce both sides — the server returns issue_id as a bigint, which the
  // pg driver hands back as a string. The URL-sync effect in TriagePage then
  // overwrites local state with Number(urlIssueId), so for a few ms after a
  // click we have selectedIssueId=8 (number) and issue.issue_id="8" (string).
  // Strict === made the row deselect itself the moment the URL sync landed.
  const selected =
    selectedIssueId != null && Number(issue.issue_id) === Number(selectedIssueId);
  const age = dayjs(issue.created_at).fromNow(true);

  // Advisory claim: show a lock chip only while the claim is live (TTL).
  // "you" when it's mine, else the short actor — the core dedup signal.
  const claimFresh = issue.claimed_by != null && isClaimFresh(issue.claimed_at);
  const claimedByMe =
    claimFresh && currentUserEmail != null && issue.claimed_by === currentUserEmail;
  const priority = issue.priority;
  // SLA aging: static priority alone can bury an old P2 behind fresh P1s.
  // Flag issues open past a per-tier age budget so within-tier urgency shows.
  const ageHours = dayjs().diff(dayjs(issue.created_at), 'hour');
  const slaHours = priority === 1 ? 24 : priority === 2 ? 24 * 7 : 24 * 14;
  const aging = ageHours > slaHours;
  const checked = !!(selectable && selectedIds?.has(issue.issue_id));

  return (
    <div
      className={selected ? 'issue-row issue-row--selected' : 'issue-row'}
      style={{
        ...style,
        padding: '8px 8px',
        borderBottom: '1px solid #f0f0f0',
        borderLeft: selected ? '4px solid #006EB7' : '4px solid transparent',
        background: selected ? '#e6f4ff' : undefined,
        boxShadow: selected ? 'inset 0 0 0 1px #006EB7' : undefined,
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        boxSizing: 'border-box',
        transition: 'background 0.15s',
      }}
      onClick={() => selectIssue(issue.issue_id)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          selectIssue(issue.issue_id);
        }
      }}
      role="button"
      tabIndex={0}
      aria-current={selected ? 'page' : undefined}
      aria-label={`Issue ${issue.observable_id}${classTag ? ` — ${classTag.label}` : ''}`}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {selectable && (
          <Checkbox
            checked={checked}
            onClick={(e) => e.stopPropagation()}
            onChange={() => onToggleSelect?.(issue.issue_id)}
            aria-label={`Select issue ${issue.observable_id} for bulk action`}
          />
        )}
        {priority != null ? (
          // Priority is now a labeled P1–P4 chip, not a bare dot — the queue is
          // priority-sorted by default, so the operator needs to read the tier
          // at a glance without hovering.
          <Tooltip title={priorityLabel[priority] || `Priority ${priority}`}>
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 3,
                fontSize: 10,
                fontWeight: 700,
                lineHeight: '16px',
                padding: '0 5px',
                borderRadius: 4,
                color: '#fff',
                backgroundColor: priorityColor[priority] || '#8c8c8c',
              }}
            >
              P{priority}
            </span>
          </Tooltip>
        ) : (
          <span style={{ display: 'inline-block', width: 8 }} />
        )}
        {nodeTypeIcon[issue.observable_info || ''] || (
          <QuestionCircleOutlined style={{ color: '#bfbfbf' }} />
        )}
        <Typography.Text strong style={{ fontFamily: 'monospace', fontSize: 13 }}>
          {issue.observable_id}
        </Typography.Text>
        {aging ? (
          <Tooltip title={`Aging — open ${age}, past the P${priority ?? '?'} SLA budget`}>
            <span style={{ color: '#d4380d', fontSize: 12, fontWeight: 600 }}>⏱ {age}</span>
          </Tooltip>
        ) : (
          <span style={{ color: '#8c8c8c', fontSize: 12 }}>{age}</span>
        )}
        {claimFresh && (
          <Tooltip
            title={
              claimedByMe
                ? 'You claimed this issue'
                : `${shortActor(issue.claimed_by)} is investigating this (${dayjs(issue.claimed_at).fromNow()})`
            }
          >
            <Tag
              icon={<LockOutlined />}
              color={claimedByMe ? 'processing' : 'warning'}
              style={{ margin: 0, marginInlineStart: 'auto', fontSize: 10, lineHeight: '16px', padding: '0 4px' }}
            >
              {claimedByMe ? 'you' : shortActor(issue.claimed_by)}
            </Tag>
          </Tooltip>
        )}
      </div>
      {/* Line 2 trimmed to the essentials operators asked for: class (shown on
          /issues; hidden on /triage by design) + location. Alerts, gateway
          peers, silent-days, confidence, sub-class hint, and last-triaged were
          chip-soup on the row — they now live in the detail pane (header,
          cohort banner, node info, triage history), which shows richer versions
          on click. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
        {!hideClassChip && (
          <Tooltip title={classTooltip}>
            <Tag
              color={classTag?.color ?? PENDING_CLASS_TAG.color}
              style={{ margin: 0, fontSize: 10, lineHeight: '16px', padding: '0 4px' }}
            >
              {classTag?.label || PENDING_CLASS_TAG.label}
            </Tag>
          </Tooltip>
        )}
        {issue.location_name && (
          <Tooltip title={issue.location_name}>
            <Typography.Text
              type="secondary"
              style={{
                fontSize: 12,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                minWidth: 0,
                flex: '0 1 auto',
              }}
            >
              {issue.location_name}
            </Typography.Text>
          </Tooltip>
        )}
        {sparklines[issue.observable_id] && (
          <Space size={4} style={{ marginLeft: 'auto' }}>
            <Tooltip title="Battery (last 14d)">
              <span><Sparkline data={sparklines[issue.observable_id].battery} color="#faad14" width={36} height={14} /></span>
            </Tooltip>
            <Tooltip title="Signal (last 14d)">
              <span><Sparkline data={sparklines[issue.observable_id].signal} color="#006EB7" width={36} height={14} /></span>
            </Tooltip>
          </Space>
        )}
      </div>
    </div>
  );
};

// Wrapped Reclassify-all button. Holds the "Without LLM" checkbox state in
// its own local hook so the parent only sees the final mode at click time.
// Without this, the checkbox would reset on every parent re-render.
const ReclassifyAllButton: React.FC<{
  onReclassifyAll: (opts: { skipLlm: boolean }) => void;
  running: boolean;
}> = ({ onReclassifyAll, running }) => {
  const [skipLlm, setSkipLlm] = useState(false);
  return (
    <Popconfirm
      title="Reclassify every open issue?"
      description={
        <div style={{ maxWidth: 320 }}>
          <div style={{ marginBottom: 8 }}>
            Re-runs the rule classifier on all open non-manual issues.
            Server work parallelizes ~30s at fleet scale; LLM predictions
            refresh in the background via one Anthropic Message Batch
            (minutes) instead of N per-issue calls.
          </div>
          <Checkbox
            checked={skipLlm}
            onChange={(e) => setSkipLlm(e.target.checked)}
          >
            Without LLM (heuristics only — bypasses Anthropic)
          </Checkbox>
        </div>
      }
      okText="Reclassify all"
      cancelText="Cancel"
      onConfirm={() => onReclassifyAll({ skipLlm })}
    >
      <Button
        type="text"
        size="small"
        icon={<SyncOutlined spin={running} />}
        loading={running}
        aria-label="Reclassify all open issues"
      >
        <span style={{ fontSize: 11 }}>Reclassify all</span>
      </Button>
    </Popconfirm>
  );
};

const IssueListPane: React.FC<IssueListPaneProps> = ({
  issues,
  loading,
  selectedIssueId,
  statusFilter,
  classFilter,
  sortOrder,
  mode = 'issues',
  currentUserEmail,
  selectable,
  selectedIds,
  onToggleSelect,
  onClearSelection,
  onBulkAction,
  bulkRunning,
  onSelectIssue,
  onStatusFilterChange,
  onClassFilterChange,
  onSortChange,
  onBackfill,
  backfillRunning,
  onReclassifyAll,
  reclassifyAllRunning,
  onCollapse,
  hasMore,
  loadingMore,
  onLoadMore,
  totalCount,
  onApplyView,
}) => {
  const title = mode === 'nodes' ? 'Triage queue' : 'Issues';
  const countLabel = mode === 'nodes' ? 'to triage' : 'issues';
  const handleSelect = useCallback((id: number) => onSelectIssue(id), [onSelectIssue]);

  const [sparklines, setSparklines] = useState<SparklineData>({});
  const sparklineFetchedRef = useRef<Set<string>>(new Set());
  const [visibleRange, setVisibleRange] = useState<[number, number]>([0, 0]);
  const [bulkActionKey, setBulkActionKey] = useState<string | null>(null);

  // Reset when the underlying issue set changes (location switch, filter
  // change, sort change). Without this, swapping queues keeps stale
  // sparklines for nodes that aren't even visible anymore.
  useEffect(() => {
    sparklineFetchedRef.current = new Set();
    setSparklines({});
  }, [issues]);

  // Visible-row fetch: only ask the server for sparklines we don't have.
  // The endpoint caps at 50 macs per call, so the slice is safe.
  useEffect(() => {
    const [start, end] = visibleRange;
    if (end <= start) return;
    const visible = issues.slice(start, end + 1);
    const newMacs = visible
      .map((i) => i.observable_id)
      .filter((mac) => mac && !sparklineFetchedRef.current.has(mac));
    if (newMacs.length === 0) return;
    newMacs.forEach((mac) => sparklineFetchedRef.current.add(mac));

    let cancelled = false;
    fetchSparklines(newMacs)
      .then((data) => { if (!cancelled) setSparklines((prev) => ({ ...prev, ...data })); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [visibleRange, issues]);

  const exportCSV = useCallback(() => {
    const headers = [
      'issue_id', 'observable_id', 'ticket_class', 'status_name', 'location_name',
      'key_name', 'autoset_name', 'priority', 'confidence', 'classifier_version',
      'classifier_notes', 'created_at', 'classified_at', 'alert_count',
    ];
    const escape = (val: unknown) => {
      if (val == null) return '';
      const str = String(val);
      return /[,"\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
    };
    const csv = [
      headers.join(','),
      ...issues.map((i) => headers.map((h) => escape((i as Record<string, unknown>)[h])).join(',')),
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${mode === 'nodes' ? 'triage' : 'issues'}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [issues, mode]);

  // Stable onRowsRendered for react-window v2's <List>. The inline arrow
  // version caused React #185 (max update depth): every parent render created
  // a new onRowsRendered, List recommitted, called it, setVisibleRange got a
  // NEW [s,e] array ref, parent re-rendered, repeat. Bail out when range is
  // unchanged so the setState becomes a no-op.
  const handleRowsRendered = useCallback(
    ({ startIndex, stopIndex }: { startIndex: number; stopIndex: number }) => {
      setVisibleRange((prev) =>
        prev[0] === startIndex && prev[1] === stopIndex ? prev : [startIndex, stopIndex],
      );
      if (onLoadMore && hasMore && !loadingMore && stopIndex >= issues.length - 10) {
        onLoadMore();
      }
    },
    [onLoadMore, hasMore, loadingMore, issues.length],
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Title bar */}
      <div style={{ padding: '8px 12px', borderBottom: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography.Text strong style={{ fontSize: 13 }}>{title}</Typography.Text>
        <Space size={4}>
          <Popover
            trigger="click"
            placement="bottomRight"
            title="What the marks mean"
            content={
              <div style={{ maxWidth: 320 }}>
                {CHIP_LEGEND.map((c) => (
                  <div key={c.label} style={{ marginBottom: 6, fontSize: 12, lineHeight: 1.4 }}>
                    <Typography.Text strong>{c.label}</Typography.Text>
                    <div style={{ color: '#8c8c8c' }}>{c.meaning}</div>
                  </div>
                ))}
                <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                  Full details (alerts, gateway cohort, silent days, confidence, history) are in the panel on the right.
                </Typography.Text>
              </div>
            }
          >
            <Tooltip title="Legend — what the row marks mean">
              <Button type="text" size="small" icon={<InfoCircleOutlined />} aria-label="Row legend" />
            </Tooltip>
          </Popover>
          <Tooltip title="Export current list as CSV">
            <Button
              type="text"
              size="small"
              icon={<DownloadOutlined />}
              onClick={exportCSV}
              disabled={issues.length === 0}
            />
          </Tooltip>
          {onBackfill && (
            <Tooltip title="Run classifier sweep now (creates issues for new alerts)">
              <Button
                type="text"
                size="small"
                icon={<ReloadOutlined spin={backfillRunning} />}
                onClick={onBackfill}
                loading={backfillRunning}
                aria-label="Run classifier sweep now"
              />
            </Tooltip>
          )}
          {onReclassifyAll && (
            // UX-061: this button touches EVERY open issue (real LLM spend).
            // Surface a visible label + Popconfirm so it can't be mistaken
            // for the lower-impact Backfill icon next to it.
            // 2026-06-09: added "Without LLM" checkbox — server skips the
            // Anthropic submit entirely on the sidecar side. Use when
            // Anthropic is rate-limited / out of credit, or for heuristic-
            // only audit runs.
            <ReclassifyAllButton
              onReclassifyAll={onReclassifyAll}
              running={reclassifyAllRunning}
            />
          )}
          {onCollapse && (
            <Button type="text" size="small" icon={<LeftOutlined style={{ fontSize: 10 }} />} onClick={onCollapse} />
          )}
        </Space>
      </div>
      {/* Filters */}
      <div style={{ padding: '8px 12px', borderBottom: '1px solid #f0f0f0' }}>
        <Space direction="vertical" size={8} style={{ width: '100%' }}>
          {/* UX-014: status filter is server-honored only on the `issues`
              route. On `nodes` (Triage queue), the server selects by
              stage='suggested' and ignores statusFilter — surfacing the
              control there made the URL param tick but the list never
              changed, so operators thought it was broken. Hide on /triage. */}
          {mode === 'issues' && (
            <Segmented<StatusFilter>
              size="small"
              block
              options={[
                { label: 'Open', value: 'open' },
                { label: 'In Progress', value: 'in_progress' },
                { label: 'All', value: 'all' },
              ]}
              value={statusFilter}
              onChange={onStatusFilterChange}
            />
          )}
          <Space size={8}>
            <Select<number | 'all'>
              size="small"
              value={classFilter ?? 'all'}
              style={{ width: 100 }}
              onChange={(v) => onClassFilterChange(v === 'all' ? null : Number(v))}
              options={[
                { label: 'Class', value: 'all' },
                ...FLEET_CLASS_IDS.map((id) => ({
                  label: classTagMap[id]?.label || String(id),
                  value: id,
                })),
              ]}
            />
            <Select<SortOrder>
              size="small"
              value={sortOrder}
              style={{ width: 110 }}
              onChange={onSortChange}
              options={[
                { label: 'Oldest first', value: 'oldest' },
                { label: 'Newest first', value: 'newest' },
                { label: 'Priority', value: 'priority' },
              ]}
            />
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {loading
                ? `loading ${countLabel}…`
                : totalCount != null && totalCount > issues.length
                ? `${issues.length} of ${totalCount} ${countLabel}`
                : `${issues.length}${hasMore ? '+' : ''} ${countLabel}`}
            </Typography.Text>
            {onApplyView && (
              <SavedViews
                currentStatus={statusFilter}
                currentClass={classFilter}
                currentSort={sortOrder}
                onApply={onApplyView}
              />
            )}
          </Space>
        </Space>
      </div>
      {/* Bulk-action bar — appears on /issues once rows are checked. Applies
          one disposition to every selected issue (gateway-cohort cleanup). */}
      {selectable && selectedIds && selectedIds.size > 0 && (
        <div style={{ padding: '8px 12px', borderBottom: '1px solid #f0f0f0', background: '#e6f4ff', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <Typography.Text strong style={{ fontSize: 12 }}>{selectedIds.size} selected</Typography.Text>
          <Select
            size="small"
            style={{ width: 170 }}
            placeholder="Apply action…"
            value={bulkActionKey ?? undefined}
            onChange={setBulkActionKey}
            options={TRIAGE_ACTIONS.map((a) => ({ value: a.key, label: a.label }))}
          />
          <Popconfirm
            title={`Apply "${TRIAGE_ACTIONS.find((a) => a.key === bulkActionKey)?.label ?? ''}" to ${selectedIds.size} issue${selectedIds.size === 1 ? '' : 's'}?`}
            okText="Apply"
            cancelText="Cancel"
            onConfirm={() => {
              const a = TRIAGE_ACTIONS.find((x) => x.key === bulkActionKey);
              if (a && onBulkAction) { onBulkAction(a); setBulkActionKey(null); }
            }}
          >
            <Button size="small" type="primary" disabled={!bulkActionKey} loading={bulkRunning}>Apply</Button>
          </Popconfirm>
          <Button size="small" onClick={onClearSelection}>Clear</Button>
        </div>
      )}
      <div style={{ flex: 1, overflow: 'hidden' }}>
        {loading ? (
          <Skeleton active paragraph={{ rows: 8 }} style={{ padding: 16 }} />
        ) : issues.length === 0 ? (
          <Empty description="No issues" style={{ padding: 32 }} />
        ) : (
          <VirtualList
            rowCount={issues.length}
            rowHeight={ROW_HEIGHT}
            rowComponent={IssueRow}
            rowProps={{ issues, selectedIssueId, selectIssue: handleSelect, sparklines, hideClassChip: mode === 'nodes', currentUserEmail, selectable, selectedIds, onToggleSelect }}
            style={{ height: '100%', width: '100%' }}
            onRowsRendered={handleRowsRendered}
          />
        )}
      </div>
    </div>
  );
};

export default IssueListPane;
